import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { opportunitiesAPI } from '../services/api';
import { Opportunity } from '../types';
import { Search, MapPin, Calendar, Eye, GraduationCap, Building, Award, Briefcase, TrendingUp } from 'lucide-react';
import { FadeIn, Stagger, StaggerItem } from '../components/animations';
import { Card, CardBody, Badge } from '../components/ui';
import { useAuth } from '../context/AuthContext';

const Opportunities: React.FC = () => {
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    type: '',
    location: '',
  });

  const { user } = useAuth();
  const canSeeViews = user?.role === 'admin' || user?.role === 'content-creator';
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchOpportunities = async (params?: any) => {
    setLoading(true);
    try {
      const response = await opportunitiesAPI.getAll(params);
      setOpportunities(response.data.data ?? []);
    } catch (error) {
      console.error('Error fetching opportunities:', error);
      setOpportunities([]);
    } finally {
      setLoading(false);
    }
  };

  // Live search: debounce text inputs, instant on dropdown change
  useEffect(() => {
    const params = Object.fromEntries(
      Object.entries(filters).filter(([_, v]) => v !== '')
    );
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchOpportunities(params), 350);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [filters.search, filters.type, filters.location]);

  const getTypeIcon = (type: string) => {
    const icons: any = {
      scholarship: <GraduationCap className="w-5 h-5" />,
      internship: <Briefcase className="w-5 h-5" />,
      training: <TrendingUp className="w-5 h-5" />,
      grant: <Award className="w-5 h-5" />,
    };
    return icons[type] || <GraduationCap className="w-5 h-5" />;
  };

  const getTypeColor = (type: string) => {
    const colors: any = {
      scholarship: 'bg-blue-100 text-blue-700',
      internship: 'bg-green-100 text-green-700',
      training: 'bg-purple-100 text-purple-700',
      grant: 'bg-orange-100 text-orange-700',
    };
    return colors[type] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <div className="mb-10">
            <h1 className="text-4xl font-bold text-gray-900 mb-3">Opportunities</h1>
            <p className="text-lg text-gray-600">Discover scholarships, internships, training programs, and grants</p>
          </div>
        </FadeIn>

        <FadeIn delay={0.1}>
          <Card className="mb-8">
            <CardBody className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    placeholder="Search opportunities"
                  />
                </div>

                <select
                  value={filters.type}
                  onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                >
                  <option value="">All Types</option>
                  <option value="scholarship">Scholarship</option>
                  <option value="internship">Internship</option>
                  <option value="training">Training</option>
                  <option value="grant">Grant</option>
                </select>

                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={filters.location}
                    onChange={(e) => setFilters({ ...filters, location: e.target.value })}
                    className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    placeholder="Location"
                  />
                </div>

                <button
                  onClick={() => {
                    const params = Object.fromEntries(
                      Object.entries(filters).filter(([_, v]) => v !== '')
                    );
                    fetchOpportunities(params);
                  }}
                  className="flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition"
                >
                  <Search className="w-5 h-5" />
                  Search
                </button>
              </div>
            </CardBody>
          </Card>
        </FadeIn>

        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-gray-600">Loading opportunities...</p>
          </div>
        ) : opportunities.length === 0 ? (
          <FadeIn delay={0.2}>
            <Card className="text-center py-16">
              <CardBody>
                <GraduationCap className="w-20 h-20 text-gray-300 mx-auto mb-4" />
                <h3 className="text-2xl font-semibold text-gray-700 mb-2">No opportunities found</h3>
                <p className="text-gray-600">Try adjusting your search filters</p>
              </CardBody>
            </Card>
          </FadeIn>
        ) : (
          <>
            <FadeIn delay={0.2}>
              <div className="mb-6 flex items-center gap-2 text-gray-600">
                <GraduationCap className="w-5 h-5" />
                <span className="font-medium">{opportunities.length} opportunities found</span>
              </div>
            </FadeIn>

            <Stagger>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {opportunities.map((opp) => (
                  <StaggerItem key={opp._id}>
                    <Link to={`/opportunities/${opp.slug || opp._id}`}>
                      <Card hover className="h-full group cursor-pointer">
                        <CardBody className="p-6">
                          <div className="flex items-start justify-between mb-4">
                            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${getTypeColor(opp.type)}`}>
                              {getTypeIcon(opp.type)}
                            </div>
                            {opp.featured && <Badge variant="warning">Featured</Badge>}
                          </div>

                          <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition line-clamp-2">
                            {opp.title}
                          </h3>

                          <div className="mb-4">
                            <span className={`inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full capitalize font-medium ${getTypeColor(opp.type)}`}>
                              {opp.type}
                            </span>
                          </div>

                          <p className="text-sm text-gray-600 mb-4 line-clamp-3">{opp.description}</p>

                          <div className="space-y-2 mb-4">
                          {opp.organization && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Building className="w-4 h-4" />
                            <span className="line-clamp-1">{opp.organization}</span>
                          </div>
                          )}
                          {opp.location && (opp.location.city || opp.location.country) && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <MapPin className="w-4 h-4" />
                            <span>{[opp.location.city, opp.location.country].filter(Boolean).join(', ')}</span>
                          </div>
                          )}
                          {opp.deadline && (
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Calendar className="w-4 h-4" />
                              <span>Deadline: {new Date(opp.deadline).toLocaleDateString()}</span>
                            </div>
                          )}
                        </div>

                        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                          <span className="text-blue-600 font-semibold text-sm group-hover:gap-2 flex items-center gap-1 transition-all">
                            View Details
                            <span className="group-hover:translate-x-1 transition-transform">→</span>
                          </span>
                        </div>
                      </CardBody>
                    </Card>
                    </Link>
                  </StaggerItem>
                ))}
              </div>
            </Stagger>
          </>
        )}
      </div>
    </div>
  );
};

export default Opportunities;
